/**
 * NEXUS UI - HexGrid
 * Honeycomb status display grid
 */

import React from 'react';
import type { BaseProps, HexCell } from './types';

export interface HexGridProps extends BaseProps {
  cells: HexCell[];
  columns?: number;
  cellSize?: number;
  onCellClick?: (cell: HexCell) => void;
}

const statusColors: Record<string, { bg: string; glow: string }> = {
  active: { bg: '#0d4f5c', glow: 'rgba(0, 212, 255, 0.6)' },
  warning: { bg: '#5c3d0d', glow: 'rgba(255, 170, 0, 0.6)' },
  critical: { bg: '#5c1a1a', glow: 'rgba(255, 51, 102, 0.6)' },
  inactive: { bg: '#1c2128', glow: 'transparent' },
};

export const HexGrid: React.FC<HexGridProps> = ({
  cells,
  columns = 6,
  cellSize = 40,
  onCellClick,
  className = '',
  style,
}) => {
  const hexHeight = cellSize * 1.15;
  const hexWidth = cellSize;

  return (
    <div
      className={`nx-hex-grid ${className}`}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, ${hexWidth}px)`,
        gap: '4px',
        padding: '8px',
        ...style,
      }}
    >
      {cells.map((cell, index) => {
        const colors = statusColors[cell.status] || statusColors.inactive;
        const row = Math.floor(index / columns);
        const isOffsetRow = row % 2 === 1;

        return (
          <div
            key={cell.id}
            className={`nx-hex-cell nx-hex-cell--${cell.status}`}
            title={cell.tooltip}
            onClick={() => onCellClick?.(cell)}
            style={{
              width: hexWidth,
              height: hexHeight,
              clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
              background: colors.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: onCellClick ? 'pointer' : 'default',
              transition: 'all 0.15s ease-out',
              boxShadow: cell.status !== 'inactive' ? `0 0 12px ${colors.glow}` : 'none',
              transform: isOffsetRow ? `translateX(${hexWidth / 2 + 2}px)` : 'none',
              animation: cell.status === 'critical' ? 'nx-pulse 1s infinite' : 'none',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = isOffsetRow
                ? `translateX(${hexWidth / 2 + 2}px) scale(1.1)`
                : 'scale(1.1)';
              e.currentTarget.style.zIndex = '10';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = isOffsetRow
                ? `translateX(${hexWidth / 2 + 2}px)`
                : 'none';
              e.currentTarget.style.zIndex = '0';
            }}
          >
            {cell.icon && (
              <span
                className="nx-hex-cell__icon"
                style={{
                  width: '16px',
                  height: '16px',
                  color:
                    cell.status === 'active'
                      ? '#00d4ff'
                      : cell.status === 'warning'
                      ? '#ffaa00'
                      : cell.status === 'critical'
                      ? '#ff3366'
                      : 'var(--nx-text-tertiary)',
                }}
              >
                {cell.icon}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default HexGrid;
